import { dayjs, type PlaceOfSafetyType } from "@thalamos/common";
import {
  FormSectionSchema,
  IncidentFormStatusSchema,
  IncidentFormTypeSchema,
  tk,
  type AdminEditReportingFieldsRequestUnbranded,
  type AdminReportingFieldKey,
  type FormSection,
  type IncidentWorkItemWithContext,
  type TranslationKey,
  type TranslationKeyAndOptions,
} from "@thalamos/vision-common";

import type { DateTimePickerOutput } from "../../components/FormBuilder/DateTimePicker/DateTimePicker.js";

export interface EditableField {
  /** Formik field name (no dots — kept index-based to avoid nested paths). */
  formKey: string;
  formId: string;
  sectionId: FormSection;
  fieldKey: AdminReportingFieldKey;
  label: TranslationKeyAndOptions;
  currentValue: string | null;
}

export type FormValues = Record<string, DateTimePickerOutput>;

/** Translation key per curated place-of-safety type. Kept as literals so `tk` type-checks. */
export const POS_TYPE_OPTION_KEYS = {
  hbPos136: tk("adminRelinkPlaceOfSafety.posType.options.hbPos136"),
  emergencyDepartment: tk(
    "adminRelinkPlaceOfSafety.posType.options.emergencyDepartment",
  ),
  returnedToMentalHealthUnit: tk(
    "adminRelinkPlaceOfSafety.posType.options.returnedToMentalHealthUnit",
  ),
  careHome: tk("adminRelinkPlaceOfSafety.posType.options.careHome"),
  custody: tk("adminRelinkPlaceOfSafety.posType.options.custody"),
  privateHome: tk("adminRelinkPlaceOfSafety.posType.options.privateHome"),
  other: tk("adminRelinkPlaceOfSafety.posType.options.other"),
} satisfies Record<PlaceOfSafetyType, TranslationKey>;

/** The current place-of-safety state of one `recordPlaceOfSafety` form, for the relink modal. */
export interface PosRelinkTarget {
  formId: string;
  currentName: string;
  /** The curated id currently linked, or null when the entry is manual. */
  currentId: string | null;
  /** The currently recorded place-of-safety type value, or null when unset. */
  currentType: string | null;
}

/**
 * Derives the place-of-safety relink target for each active `recordPlaceOfSafety` form,
 * keyed by form id so the edit-reporting-fields page can launch a per-POS relink modal.
 */
export function buildPosRelinkTargets(
  incident: IncidentWorkItemWithContext,
  fallbackName: string,
): PosRelinkTarget[] {
  return incident.forms
    .filter(
      (f) =>
        f.formType === IncidentFormTypeSchema.enum.recordPlaceOfSafety &&
        f.status === IncidentFormStatusSchema.enum.active,
    )
    .flatMap((form) => {
      const mainSection = form.sections.find(
        (s) => s.id === FormSectionSchema.enum.recordPlaceOfSafetyMain,
      );
      if (!mainSection) {
        return [];
      }
      const pos = mainSection.data?.placeOfSafety as
        { id?: string; name?: string } | undefined;
      const type = mainSection.data?.placeOfSafetyType as
        { value?: string } | undefined;
      return [
        {
          formId: form.id,
          currentName: pos?.name ?? fallbackName,
          currentId: typeof pos?.id === "string" ? pos.id : null,
          currentType: typeof type?.value === "string" ? type.value : null,
        },
      ];
    });
}

export interface PendingEdit {
  field: EditableField;
  from: string | null;
  to: string;
}

/** Reads a `{ value: string }`-shaped datetime field from section data. */
export function readFieldValue(
  data: Record<string, unknown> | null | undefined,
  fieldKey: string,
): string | null {
  const field = data?.[fieldKey] as { value?: string } | undefined;
  return typeof field?.value === "string" ? field.value : null;
}

export function toInitialValue(current: string | null): DateTimePickerOutput {
  return current ? { value: current } : "";
}

export function outputToIso(
  output: DateTimePickerOutput | undefined,
): string | null {
  if (!output) {
    return null;
  }
  return typeof output.value === "string" ? output.value : null;
}

/** Wraps a translation key in the object form the date picker labels require. */
export function tko(key: Parameters<typeof tk>[0]): TranslationKeyAndOptions {
  return { translationKey: tk(key) };
}

export function formatDateTime(iso: string | null, notSet: string): string {
  if (!iso) {
    return notSet;
  }
  return dayjs.utc(iso).tz("Europe/London").format("DD/MM/YYYY HH:mm");
}

const isActiveForm = (status: string) =>
  status === IncidentFormStatusSchema.enum.active;

/**
 * Derives the editable reporting-time fields from the incident: the single time of detention,
 * one arrival time per (active) place-of-safety form, and the time police left — on the finish
 * form, and on the pre-end form when the incident was ended early with a time recorded.
 *
 * Only ACTIVE forms are considered, mirroring the server which rejects edits to non-active
 * forms — so the UI never surfaces a field that could never be saved.
 */
export function buildEditableFields(
  incident: IncidentWorkItemWithContext,
  posFallback: string,
): {
  detention: EditableField | null;
  arrivals: EditableField[];
  departures: EditableField[];
} {
  const detentionForm = incident.forms.find(
    (f) =>
      f.formType === IncidentFormTypeSchema.enum.section136 &&
      isActiveForm(f.status),
  );
  const detentionSection = detentionForm?.sections.find(
    (s) => s.id === FormSectionSchema.enum.detentionDetails,
  );
  const detention: EditableField | null =
    detentionForm && detentionSection
      ? {
          formKey: "detention",
          formId: detentionForm.id,
          sectionId: FormSectionSchema.enum.detentionDetails,
          fieldKey: "dateTimeOfDetention",
          label: {
            translationKey: tk("adminEditReportingFields.detentionLabel"),
          },
          currentValue: readFieldValue(
            detentionSection.data,
            "dateTimeOfDetention",
          ),
        }
      : null;

  const arrivals: EditableField[] = incident.forms
    .filter(
      (f) =>
        f.formType === IncidentFormTypeSchema.enum.recordPlaceOfSafety &&
        isActiveForm(f.status),
    )
    .flatMap((form, index) => {
      const arrivalSection = form.sections.find(
        (s) => s.id === FormSectionSchema.enum.recordPlaceOfSafetyArrival,
      );
      if (!arrivalSection) {
        return [];
      }
      const mainSection = form.sections.find(
        (s) => s.id === FormSectionSchema.enum.recordPlaceOfSafetyMain,
      );
      const posName =
        (mainSection?.data?.placeOfSafety as { name?: string } | undefined)
          ?.name ?? posFallback;
      return [
        {
          formKey: `arrival_${String(index)}`,
          formId: form.id,
          sectionId: FormSectionSchema.enum.recordPlaceOfSafetyArrival,
          fieldKey: "arrivalDateTime" as const,
          label: {
            translationKey: tk("adminEditReportingFields.arrivalLabel"),
            translationOptions: { posName },
          },
          currentValue: readFieldValue(arrivalSection.data, "arrivalDateTime"),
        },
      ];
    });

  const finishForm = incident.forms.find(
    (f) =>
      f.formType === IncidentFormTypeSchema.enum.finishForm &&
      isActiveForm(f.status),
  );
  const finishSection = finishForm?.sections.find(
    (s) => s.id === FormSectionSchema.enum.finishForm,
  );
  const finishLeft: EditableField[] =
    finishForm && finishSection
      ? [
          {
            formKey: "left",
            formId: finishForm.id,
            sectionId: FormSectionSchema.enum.finishForm,
            fieldKey: "dateTimeLeft",
            label: {
              translationKey: tk("adminEditReportingFields.leftLabel"),
            },
            currentValue: readFieldValue(finishSection.data, "dateTimeLeft"),
          },
        ]
      : [];

  const preEndForm = incident.forms.find(
    (f) =>
      f.formType === IncidentFormTypeSchema.enum.preEndIncident &&
      isActiveForm(f.status),
  );
  const leavePos = readFieldValue(
    preEndForm?.sections.find(
      (s) => s.id === FormSectionSchema.enum.preEndIncident,
    )?.data,
    "leavePos",
  );
  // An admin corrects a time the officer recorded and never supplies one they did not, so a
  // pre-end form without a time gets no field.
  const preEndLeft: EditableField[] =
    preEndForm && leavePos !== null
      ? [
          {
            formKey: "leftEarlyEnd",
            formId: preEndForm.id,
            sectionId: FormSectionSchema.enum.preEndIncident,
            fieldKey: "leavePos",
            label: {
              translationKey: tk("adminEditReportingFields.leftEarlyEndLabel"),
            },
            currentValue: leavePos,
          },
        ]
      : [];

  return { detention, arrivals, departures: [...finishLeft, ...preEndLeft] };
}

/**
 * Computes which fields actually changed. A field counts as changed only when it now has a
 * value AND that value differs from the current one at MINUTE granularity — the picker only
 * captures minutes, so a stored value carrying seconds must not register as a spurious edit
 * (nor have its seconds silently truncated).
 */
export function computeEdits(
  allFields: EditableField[],
  values: FormValues,
): PendingEdit[] {
  return allFields.flatMap((field) => {
    const newIso = outputToIso(values[field.formKey]);
    if (newIso === null) {
      return [];
    }
    const unchanged =
      field.currentValue !== null &&
      dayjs(newIso).isSame(dayjs(field.currentValue), "minute");
    if (unchanged) {
      return [];
    }
    return [{ field, from: field.currentValue, to: newIso }];
  });
}

/** Initial Formik values: each field seeded from its current stored value. */
export function buildInitialValues(allFields: EditableField[]): FormValues {
  const values: FormValues = {};
  for (const field of allFields) {
    values[field.formKey] = toInitialValue(field.currentValue);
  }
  return values;
}

/** Maps the confirmed edits + metadata into the request body sent to the server. */
export function editsToRequestBody(
  edits: PendingEdit[],
  reason: string,
  ccmNumber: string,
): AdminEditReportingFieldsRequestUnbranded {
  return {
    reason: reason.trim(),
    ccmNumber: ccmNumber.trim(),
    edits: edits.map(({ field, to }) => ({
      formId: field.formId,
      sectionId: field.sectionId,
      fieldKey: field.fieldKey,
      newValue: to,
    })),
  };
}
